import React, { Suspense, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { Toaster as Sonner } from '@knitknot/shared/components/ui/sonner';
import { TooltipProvider } from '@knitknot/shared/components/ui/tooltip';
import { ConfirmProvider } from '@knitknot/shared/components/ui/confirm-dialog';
import { PageLoader } from '@knitknot/shared/components/ui/page-loader';
import ProtectedRoute from '@knitknot/shared/components/auth/protected-route';
import { LoggerProvider } from '@/contexts/LoggerContext';
import { PageTimingProvider } from '@/contexts/PageTimingContext';
import { ConsoleLayout } from './layouts/ConsoleLayout';
import { queryClient, PERSIST_MAX_AGE_MS } from '@/lib/queryClient';
import { didRestoreFromDisk, queryPersister, shouldDehydrateQuery } from '@/lib/queryPersister';
import { markCacheWarm } from '@/lib/rum';
import { resolveLegacyConsoleLocation } from '@/lib/legacyRoutes';
import { consoleRoutes } from '@/lib/routes';
import { parseSourceByDomainPath } from './pages/console/sources/sourcesShared';
import { OptimizationGate } from './pages/console/optimization/OptimizationGate';
import { AgentPublishGate, PublishIndexRoute } from './pages/console/publish/AgentPublishGate';
import { trackIaMetric } from '@/lib/rum';
import { FactLedgerBetaRoute } from './layouts/FactLedgerBetaRoute';

function lazyRetry<T extends React.ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return React.lazy(() =>
    factory().catch((error: unknown) => {
      const alreadyRetried = sessionStorage.getItem('chunk_retry');
      if (!alreadyRetried) {
        sessionStorage.setItem('chunk_retry', '1');
        const url = new URL(window.location.href);
        url.searchParams.set('_cb', Date.now().toString());
        window.location.replace(url.toString());
        return new Promise(() => {});
      }
      sessionStorage.removeItem('chunk_retry');
      throw error;
    }),
  );
}

const SignIn = lazyRetry(() => import('./pages/auth/SignIn'));
const Callback = lazyRetry(() => import('./pages/auth/Callback'));
const InviteAccept = lazyRetry(() => import('./pages/auth/InviteAccept'));
const NoWorkspace = lazyRetry(() => import('./pages/auth/NoWorkspace'));
const McpConnect = lazyRetry(() => import('./pages/auth/McpConnect'));
const NotFound = lazyRetry(() => import('./pages/NotFound'));

const OverviewPage = lazyRetry(() => import('./pages/console/home/OverviewPage'));
const VisibilityRankingPage = lazyRetry(() => import('./pages/console/home/VisibilityRankingPage'));
const OnboardingPage = lazyRetry(() => import('./pages/console/onboarding/OnboardingPage'));

const QuestionsPage = lazyRetry(() => import('./pages/console/prompts/QuestionsPage'));
const QueryFanoutPage = lazyRetry(() => import('./pages/console/prompts/QueryFanoutPage'));
const QueryFanoutGroupPage = lazyRetry(() => import('./pages/console/prompts/QueryFanoutGroupPage'));
const TopicDetailPage = lazyRetry(() => import('./pages/console/prompts/TopicDetailPage'));
const JourneysPage = lazyRetry(() => import('./pages/console/prompts/JourneysPage'));
const JourneyDetailPage = lazyRetry(() => import('./pages/console/prompts/JourneyDetailPage'));
const CompetitorDetailPage = lazyRetry(() => import('./pages/console/context/CompetitorDetailPage'));
const CompetitorProductPromptsPage = lazyRetry(() => import('./pages/console/context/CompetitorProductPromptsPage'));
const CapabilityDetailPage = lazyRetry(() => import('./pages/console/evaluations/CapabilityDetailPage'));
const BuyingRoleDetailPage = lazyRetry(() => import('./pages/console/context/PersonaDetailPage'));
const ProductDetailPage = lazyRetry(() => import('./pages/console/context/ProductDetailPage'));
const KeywordDetailPage = lazyRetry(() => import('./pages/console/context/KeywordDetailPage'));
const ReviewCandidateDetailPage = lazyRetry(() => import('./pages/console/context/DiscoveredDetailPage'));
const ThemeDetailPage = lazyRetry(() => import('./pages/console/context/ThemeDetailPage'));
const FactDetailPage = lazyRetry(() => import('./pages/console/context/FactDetailPage'));
const FactProposalDetailPage = lazyRetry(() => import('./pages/console/context/FactProposalDetailPage'));

// Optimize › Updates — the managed Agent-version workflow. It stays fail-closed by
// `managed_optimization_enabled`, but no longer changes the global rail shape.
const OptimizationPage = lazyRetry(() => import('./pages/console/optimization/OptimizationPage'));
const OptimizationCycleDetailPage = lazyRetry(() => import('./pages/console/optimization/CycleDetailPage'));
const OptimizationPolicySettings = lazyRetry(() => import('./pages/console/optimization/PolicySettings'));

// Records — first narrow, gated typed Fact Ledger surface
const RecordsPage = lazyRetry(() => import('./pages/console/records/RecordsPage'));
const RecordDetailPage = lazyRetry(() => import('./pages/console/records/RecordDetailPage'));

const BenchmarksPage = lazyRetry(() => import('./pages/console/benchmarks/BenchmarksPage'));
const AnswerDetailPage = lazyRetry(() => import('./pages/console/evaluations/AnswerDetailPage'));
const QuestionDetailPage = lazyRetry(() => import('./pages/console/evaluations/QuestionDetailPage'));
const CoverageDrillPage = lazyRetry(() => import('./pages/console/evaluations/CoverageDrillPage'));
const PeriodDetailPage = lazyRetry(() => import('./pages/console/periods/PeriodDetailPage'));
const PeriodSourceChangesPage = lazyRetry(() => import('./pages/console/periods/PeriodSourceChangesPage'));
const PeriodOwnedPageChangesPage = lazyRetry(() => import('./pages/console/periods/PeriodOwnedPageChangesPage'));
const PeriodTransitionChangesPage = lazyRetry(() => import('./pages/console/periods/PeriodTransitionChangesPage'));
const ReportEditorPage = lazyRetry(() => import('./pages/console/admin/ReportEditorPage'));

const SourcesPage = lazyRetry(() => import('./pages/console/sources/SourcesPage'));
const AuthorityPage = lazyRetry(() => import('./pages/console/sources/AuthorityPage'));
const AnswerClaimsPage = lazyRetry(() => import('./pages/console/sources/AnswerClaimsPage'));
const ClaimTopicPage = lazyRetry(() => import('./pages/console/sources/ClaimTopicPage'));
const PageClaimsPage = lazyRetry(() => import('./pages/console/site/PageClaimsPage'));
const ThemesPage = lazyRetry(() => import('./pages/console/sources/ThemesPage'));
const FactLedgerPage = lazyRetry(() => import('./pages/console/company-record/FactLedgerPage'));
const SearchDemandPage = lazyRetry(() => import('./pages/console/company-record/SearchDemandPage'));
const SearchDemandTopicPage = lazyRetry(() => import('./pages/console/company-record/SearchDemandTopicPage'));
const DiscoveriesPage = lazyRetry(() => import('./pages/console/company-record/DiscoveriesPage'));
const SourcesOperationsPage = lazyRetry(() => import('./pages/console/site/SourcesOperationsPage'));
const AgentToolCallsPage = lazyRetry(() => import('./pages/console/site/AgentToolCallsPage'));
const SiteCheckDetailPage = lazyRetry(() => import('./pages/console/sources/SiteCheckDetailPage'));
const SourceDetailPage = lazyRetry(() => import('./pages/console/sources/detail/SourceDetailPage'));
const SourcePagesPage = lazyRetry(() => import('./pages/console/sources/SourcePagesPage'));
const CoveragePagesPage = lazyRetry(() => import('./pages/console/sources/CoveragePagesPage'));
const VocabTermPagesPage = lazyRetry(() => import('./pages/console/sources/VocabTermPagesPage'));
const AgentLedgerPage = lazyRetry(() => import('./pages/console/publish/AgentLedgerPage'));
const PublishInterfacesPage = lazyRetry(() => import('./pages/console/publish/PublishInterfacesPage'));

const IssuesPage = lazyRetry(() => import('./pages/console/issues/IssuesPage'));
const IssueDetailPage = lazyRetry(() => import('./pages/console/issues/IssueDetailPage'));
const PlaybookListPage = lazyRetry(() => import('./pages/console/playbook/PlaybookListPage'));
const PlaybookDetailPage = lazyRetry(() => import('./pages/console/playbook/PlaybookDetailPage'));

const SettingsLayout = lazyRetry(() => import('./pages/console/admin/SettingsLayout'));
const WorkspaceSettings = lazyRetry(() => import('./pages/console/admin/WorkspaceSettings'));
const TeamSettings = lazyRetry(() => import('./pages/console/admin/TeamSettings'));
const UserProfile = lazyRetry(() => import('./pages/console/admin/UserProfile'));
const IntegrationsPage = lazyRetry(() => import('./pages/console/settings/IntegrationsPage'));
const McpSettingsPage = lazyRetry(() => import('./pages/console/settings/McpSettingsPage'));

function LegacyConsoleRedirect() {
  const location = useLocation();
  const target = resolveLegacyConsoleLocation(location.pathname, location.search, location.hash);
  useEffect(() => {
    if (target) trackIaMetric('ia-legacy-redirect', location.pathname);
  }, [location.pathname, target]);
  return target ? <Navigate replace to={target} /> : <NotFound />;
}

function SourceByDomainRoute() {
  const location = useLocation();
  const { urlHash } = parseSourceByDomainPath(location.pathname);
  return urlHash ? <SourceDetailPage /> : <SourcePagesPage />;
}

const App = () => (
  <HelmetProvider>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: queryPersister,
        buster: __BUILD_ID__,
        maxAge: PERSIST_MAX_AGE_MS,
        dehydrateOptions: { shouldDehydrateQuery },
      }}
      onSuccess={() => markCacheWarm(didRestoreFromDisk())}
      onError={() => markCacheWarm(false)}
    >
      <TooltipProvider>
        <ConfirmProvider>
          <Sonner
            toastOptions={{
              className: 'knitknot-toast bg-card text-foreground border border-border shadow-md',
              duration: 4000,
            }}
            position="bottom-right"
          />
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <PageTimingProvider>
            <LoggerProvider>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/signin" element={<SignIn />} />
                  <Route path="/signup" element={<Navigate to="/signin" replace />} />
                  <Route path="/callback" element={<Callback />} />
                  <Route path="/invite/:token" element={<InviteAccept />} />
                  <Route path="/mcp-connect" element={<McpConnect />} />

                  <Route
                    element={
                      <ProtectedRoute>
                        <ConsoleLayout />
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<Navigate to={consoleRoutes.overview} replace />} />
                    <Route path="no-workspace" element={<NoWorkspace />} />
                    <Route path="overview" element={<OverviewPage />} />
                    <Route path="overview/visibility-ranking" element={<VisibilityRankingPage />} />
                    <Route path="onboarding" element={<OnboardingPage />} />

                    <Route path="facts" element={<Navigate replace to={consoleRoutes.knowledge.profile} />} />
                    <Route path="facts/profile" element={<FactLedgerPage view="profile" />} />
                    <Route path="facts/authority" element={<AuthorityPage />} />
                    <Route path="facts/fact-ledger" element={<FactLedgerPage />} />
                    <Route element={<FactLedgerBetaRoute />}>
                      <Route path="records" element={<RecordsPage />} />
                      <Route path="records/:recordId" element={<RecordDetailPage />} />
                    </Route>
                    <Route path="facts/fact-ledger/journeys" element={<JourneysPage />} />
                    <Route path="facts/fact-ledger/journeys/:journeyId" element={<JourneyDetailPage />} />
                    <Route path="facts/search-demand" element={<SearchDemandPage />} />
                    <Route path="facts/search-demand/topics/:topicId" element={<SearchDemandTopicPage />} />
                    <Route path="facts/fact-ledger/:section" element={<FactLedgerPage />} />
                    <Route path="facts/fact-ledger/capabilities/:feature_id" element={<CapabilityDetailPage />} />
                    <Route path="facts/fact-ledger/capabilities/:feature_id/:tab" element={<CapabilityDetailPage />} />
                    <Route
                      path="facts/fact-ledger/capabilities/by-name/:feature_name"
                      element={<CapabilityDetailPage />}
                    />
                    <Route
                      path="facts/fact-ledger/capabilities/by-name/:feature_name/:tab"
                      element={<CapabilityDetailPage />}
                    />
                    <Route path="facts/fact-ledger/decision-criteria/:feature_id" element={<CapabilityDetailPage />} />
                    <Route
                      path="facts/fact-ledger/decision-criteria/:feature_id/:tab"
                      element={<CapabilityDetailPage />}
                    />
                    <Route
                      path="facts/fact-ledger/decision-criteria/by-name/:feature_name"
                      element={<CapabilityDetailPage />}
                    />
                    <Route
                      path="facts/fact-ledger/decision-criteria/by-name/:feature_name/:tab"
                      element={<CapabilityDetailPage />}
                    />
                    <Route path="facts/fact-ledger/buyer-roles/:personaSlug" element={<BuyingRoleDetailPage />} />
                    <Route path="facts/fact-ledger/buyer-roles/:personaSlug/:tab" element={<BuyingRoleDetailPage />} />
                    <Route path="facts/fact-ledger/products/:subjectId" element={<ProductDetailPage />} />
                    <Route path="facts/fact-ledger/products/:subjectId/:tab" element={<ProductDetailPage />} />
                    <Route path="facts/fact-ledger/competitors/:competitor_id" element={<CompetitorDetailPage />} />
                    <Route
                      path="facts/fact-ledger/competitors/:competitor_id/products/:offering_id/prompts"
                      element={<CompetitorProductPromptsPage />}
                    />
                    <Route
                      path="facts/fact-ledger/competitors/:competitor_id/:tab"
                      element={<CompetitorDetailPage />}
                    />
                    <Route path="facts/fact-ledger/themes/:themeId" element={<ThemeDetailPage />} />
                    <Route path="facts/fact-ledger/themes/:themeId/:tab" element={<ThemeDetailPage />} />
                    <Route path="facts/fact-ledger/facts/:kind/:factId" element={<FactDetailPage />} />
                    <Route path="facts/discoveries" element={<DiscoveriesPage />} />
                    <Route path="facts/discoveries/fact-proposals/:proposalId" element={<FactProposalDetailPage />} />
                    <Route path="facts/discoveries/:candidateId" element={<ReviewCandidateDetailPage />} />
                    <Route path="facts/discoveries/:candidateId/:tab" element={<ReviewCandidateDetailPage />} />
                    <Route path="knowledge" element={<LegacyConsoleRedirect />} />
                    <Route path="knowledge/*" element={<LegacyConsoleRedirect />} />
                    <Route path="knowledge/sources" element={<LegacyConsoleRedirect />} />
                    <Route path="knowledge/sources/*" element={<LegacyConsoleRedirect />} />

                    <Route path="prompts" element={<QuestionsPage />} />
                    <Route path="prompts/topics/:topicId" element={<TopicDetailPage />} />
                    <Route path="prompts/topics/keywords/:keyword" element={<KeywordDetailPage />} />
                    <Route path="prompts/questions/:promptId" element={<QuestionDetailPage />} />
                    <Route path="demand" element={<LegacyConsoleRedirect />} />
                    <Route path="demand/topics" element={<LegacyConsoleRedirect />} />
                    <Route path="demand/questions" element={<LegacyConsoleRedirect />} />
                    <Route path="demand/query-fanout" element={<LegacyConsoleRedirect />} />
                    <Route path="demand/*" element={<LegacyConsoleRedirect />} />

                    <Route path="benchmarks" element={<BenchmarksPage />} />
                    <Route path="benchmarks/model" element={<LegacyConsoleRedirect />} />
                    <Route path="benchmarks/model/facts" element={<LegacyConsoleRedirect />} />
                    <Route path="benchmarks/model/products" element={<LegacyConsoleRedirect />} />
                    <Route path="benchmarks/model/capabilities" element={<LegacyConsoleRedirect />} />
                    <Route path="benchmarks/model/requirements" element={<LegacyConsoleRedirect />} />
                    <Route path="benchmarks/model/buying-committee" element={<LegacyConsoleRedirect />} />
                    <Route path="benchmarks/model/review-queue" element={<LegacyConsoleRedirect />} />
                    <Route path="benchmarks/model/:section" element={<LegacyConsoleRedirect />} />
                    <Route path="benchmarks/topics" element={<LegacyConsoleRedirect />} />
                    <Route path="benchmarks/topics/:topicId" element={<LegacyConsoleRedirect />} />
                    <Route path="benchmarks/topics/keywords/:keyword" element={<LegacyConsoleRedirect />} />
                    <Route path="benchmarks/model/journeys/:journeyId" element={<LegacyConsoleRedirect />} />
                    <Route path="benchmarks/model/competitors/:competitor_id" element={<LegacyConsoleRedirect />} />
                    <Route
                      path="benchmarks/model/competitors/:competitor_id/:tab"
                      element={<LegacyConsoleRedirect />}
                    />
                    <Route path="benchmarks/model/capabilities/:feature_id" element={<LegacyConsoleRedirect />} />
                    <Route path="benchmarks/model/capabilities/:feature_id/:tab" element={<LegacyConsoleRedirect />} />
                    <Route
                      path="benchmarks/model/capabilities/by-name/:feature_name"
                      element={<LegacyConsoleRedirect />}
                    />
                    <Route
                      path="benchmarks/model/capabilities/by-name/:feature_name/:tab"
                      element={<LegacyConsoleRedirect />}
                    />
                    <Route path="benchmarks/model/buying-committee/:personaSlug" element={<LegacyConsoleRedirect />} />
                    <Route
                      path="benchmarks/model/buying-committee/:personaSlug/:tab"
                      element={<LegacyConsoleRedirect />}
                    />
                    <Route path="benchmarks/model/products/:subjectId" element={<LegacyConsoleRedirect />} />
                    <Route path="benchmarks/model/products/:subjectId/:tab" element={<LegacyConsoleRedirect />} />
                    <Route path="benchmarks/model/review-queue/:candidateId" element={<LegacyConsoleRedirect />} />
                    <Route path="benchmarks/model/review-queue/:candidateId/:tab" element={<LegacyConsoleRedirect />} />
                    <Route path="benchmarks/model/themes/:themeId" element={<LegacyConsoleRedirect />} />
                    <Route path="benchmarks/model/themes/:themeId/:tab" element={<LegacyConsoleRedirect />} />
                    <Route path="benchmarks/model/fact-proposals/:proposalId" element={<LegacyConsoleRedirect />} />
                    <Route path="benchmarks/model/facts/:kind/:factId" element={<LegacyConsoleRedirect />} />

                    <Route path="benchmarks/runs" element={<LegacyConsoleRedirect />} />
                    <Route path="benchmarks/answers/:evaluation_id" element={<AnswerDetailPage />} />
                    <Route path="benchmarks/questions" element={<LegacyConsoleRedirect />} />
                    <Route path="benchmarks/questions/:promptId" element={<LegacyConsoleRedirect />} />
                    <Route path="benchmarks/coverage/:level" element={<CoverageDrillPage />} />
                    <Route path="benchmarks/periods/:periodId" element={<PeriodDetailPage />} />
                    <Route
                      path="benchmarks/periods/:periodId/changes/:evaluationId/:kind/sources"
                      element={<PeriodSourceChangesPage />}
                    />
                    <Route
                      path="benchmarks/periods/:periodId/changes/matrix/:kind/:from/:to"
                      element={<PeriodTransitionChangesPage />}
                    />
                    <Route
                      path="benchmarks/periods/:periodId/pages/:urlHash"
                      element={<PeriodOwnedPageChangesPage />}
                    />
                    <Route path="benchmarks/reports" element={<LegacyConsoleRedirect />} />
                    <Route path="benchmarks/reports/:reportId/edit" element={<ReportEditorPage />} />


                    <Route path="intelligence" element={<Navigate replace to={consoleRoutes.data.sources} />} />
                    <Route path="intelligence/query-fan-out" element={<QueryFanoutPage />} />
                    <Route path="intelligence/query-fan-out/:groupKey" element={<QueryFanoutGroupPage />} />
                    <Route path="intelligence/queries" element={<LegacyConsoleRedirect />} />
                    <Route path="intelligence/queries/*" element={<LegacyConsoleRedirect />} />
                    <Route path="intelligence/sources" element={<SourcesPage />} />
                    <Route path="intelligence/authority" element={<LegacyConsoleRedirect />} />
                    <Route path="intelligence/sources/domains/*" element={<SourceByDomainRoute />} />
                    <Route path="intelligence/sources/:urlHash" element={<SourceDetailPage />} />
                    <Route path="intelligence/claims/answers" element={<AnswerClaimsPage />} />
                    <Route path="intelligence/claims/answers/topics" element={<ClaimTopicPage />} />
                    <Route path="intelligence/claims/pages" element={<PageClaimsPage />} />
                    <Route path="intelligence/vocabulary" element={<SourcesOperationsPage />} />
                    <Route path="intelligence/vocabulary/pages" element={<VocabTermPagesPage />} />
                    <Route path="intelligence/themes" element={<ThemesPage />} />
                    <Route path="intelligence/vocabulary/terms" element={<LegacyConsoleRedirect />} />
                    <Route path="intelligence/vocabulary/terms/pages" element={<LegacyConsoleRedirect />} />
                    <Route path="intelligence/vocabulary/themes" element={<LegacyConsoleRedirect />} />
                    <Route path="intelligence/language/*" element={<LegacyConsoleRedirect />} />
                    <Route path="intelligence/issues" element={<IssuesPage />} />
                    <Route path="intelligence/issues/:issueId" element={<IssueDetailPage />} />

                    <Route path="content" element={<PublishIndexRoute />} />
                    <Route path="content/coverage" element={<SourcesOperationsPage />} />
                    <Route path="content/coverage/pages" element={<CoveragePagesPage />} />
                    <Route path="content/playbooks" element={<PlaybookListPage />} />
                    <Route path="content/playbooks/:playbook_id" element={<PlaybookDetailPage />} />
                    <Route
                      path="content/updates"
                      element={
                        <OptimizationGate>
                          <OptimizationPage />
                        </OptimizationGate>
                      }
                    />
                    <Route
                      path="content/updates/policy"
                      element={
                        <OptimizationGate>
                          <OptimizationPolicySettings />
                        </OptimizationGate>
                      }
                    />
                    <Route
                      path="content/updates/cycles/:cycleId"
                      element={
                        <OptimizationGate>
                          <OptimizationCycleDetailPage />
                        </OptimizationGate>
                      }
                    />

                    <Route
                      path="content/agent-ledger"
                      element={
                        <AgentPublishGate>
                          <AgentLedgerPage />
                        </AgentPublishGate>
                      }
                    />
                    <Route path="content/pages" element={<SourcesOperationsPage />} />
                    <Route path="content/pages/:urlHash" element={<SourceDetailPage />} />
                    <Route
                      path="content/interfaces"
                      element={
                        <AgentPublishGate>
                          <PublishInterfacesPage />
                        </AgentPublishGate>
                      }
                    />
                    <Route path="content/access" element={<SourcesOperationsPage />} />
                    <Route path="content/access/checks/:domain/:checkId" element={<SiteCheckDetailPage />} />
                    <Route
                      path="content/delivery"
                      element={
                        <AgentPublishGate>
                          <SourcesOperationsPage />
                        </AgentPublishGate>
                      }
                    />

                    <Route path="optimize" element={<LegacyConsoleRedirect />} />
                    <Route path="optimize/*" element={<LegacyConsoleRedirect />} />
                    <Route path="publish" element={<LegacyConsoleRedirect />} />
                    <Route path="publish/*" element={<LegacyConsoleRedirect />} />
                    <Route path="data" element={<LegacyConsoleRedirect />} />
                    <Route path="data/*" element={<LegacyConsoleRedirect />} />

                    <Route path="diagnosis" element={<LegacyConsoleRedirect />} />
                    <Route path="diagnosis/*" element={<LegacyConsoleRedirect />} />

                    <Route path="fact-ledger" element={<LegacyConsoleRedirect />} />
                    <Route path="fact-ledger/*" element={<LegacyConsoleRedirect />} />
                    <Route path="answer-evidence" element={<LegacyConsoleRedirect />} />
                    <Route path="answer-evidence/*" element={<LegacyConsoleRedirect />} />
                    <Route path="company-record" element={<LegacyConsoleRedirect />} />
                    <Route path="company-record/*" element={<LegacyConsoleRedirect />} />
                    <Route path="sources" element={<LegacyConsoleRedirect />} />
                    <Route path="sources/*" element={<LegacyConsoleRedirect />} />

                    <Route path="site" element={<LegacyConsoleRedirect />} />
                    <Route path="site/*" element={<LegacyConsoleRedirect />} />

                    <Route path="traffic" element={<Navigate replace to={consoleRoutes.traffic.agents} />} />
                    <Route path="traffic/agents" element={<SourcesOperationsPage />} />
                    <Route path="traffic/tools" element={<AgentToolCallsPage />} />
                    <Route path="traffic/referrals" element={<SourcesOperationsPage />} />
                    <Route path="traffic/*" element={<LegacyConsoleRedirect />} />

                    <Route path="issues" element={<LegacyConsoleRedirect />} />
                    <Route path="issues/:issueId" element={<LegacyConsoleRedirect />} />
                    <Route path="playbooks" element={<LegacyConsoleRedirect />} />
                    <Route path="playbooks/:playbook_id" element={<LegacyConsoleRedirect />} />

                    <Route path="settings" element={<SettingsLayout />}>
                      <Route index element={<Navigate to="workspace" replace />} />
                      <Route path="workspace" element={<WorkspaceSettings />} />
                      <Route path="team" element={<TeamSettings />} />
                      <Route path="profile" element={<UserProfile />} />
                      <Route path="api" element={<LegacyConsoleRedirect />} />
                      <Route path="integrations" element={<IntegrationsPage />} />
                      <Route path="integrations/:integrationType" element={<IntegrationsPage />} />
                      <Route path="mcp" element={<McpSettingsPage />} />
                      <Route path="general" element={<LegacyConsoleRedirect />} />
                      <Route path="api-keys" element={<LegacyConsoleRedirect />} />
                    </Route>

                    {/* Compatibility is redirect-only: no retired page body or
                        navigation shell renders under these prefixes. */}
                    <Route path="home" element={<LegacyConsoleRedirect />} />
                    <Route path="ai-visits" element={<LegacyConsoleRedirect />} />
                    <Route path="benchmarks/test-design" element={<LegacyConsoleRedirect />} />
                    <Route path="benchmarks/test-design/*" element={<LegacyConsoleRedirect />} />
                    <Route path="benchmarks/query-fanout" element={<LegacyConsoleRedirect />} />
                    <Route path="prompts/*" element={<LegacyConsoleRedirect />} />
                    <Route path="runs/*" element={<LegacyConsoleRedirect />} />
                    <Route path="optimization" element={<LegacyConsoleRedirect />} />
                    <Route path="optimization/*" element={<LegacyConsoleRedirect />} />
                    <Route path="content/*" element={<LegacyConsoleRedirect />} />
                    <Route path="mirror" element={<LegacyConsoleRedirect />} />
                    <Route path="mirror/:urlHash" element={<LegacyConsoleRedirect />} />
                    <Route path="sources/pages" element={<LegacyConsoleRedirect />} />
                    <Route path="sources/coverage/*" element={<LegacyConsoleRedirect />} />
                    <Route path="sources/crawlers/*" element={<LegacyConsoleRedirect />} />
                    <Route path="sources/traffic" element={<LegacyConsoleRedirect />} />
                    <Route path="sources/delivery" element={<LegacyConsoleRedirect />} />
                    <Route path="sources/by-domain/*" element={<LegacyConsoleRedirect />} />
                    <Route path="sources/health/:domain/:checkId" element={<LegacyConsoleRedirect />} />
                    <Route path="sources/coverage/pages" element={<LegacyConsoleRedirect />} />
                    <Route path="sources/coverage/vocab" element={<LegacyConsoleRedirect />} />
                    <Route path="sources/:urlHash" element={<LegacyConsoleRedirect />} />

                    <Route path="*" element={<NotFound />} />
                  </Route>
                </Routes>
              </Suspense>
            </LoggerProvider>
            </PageTimingProvider>
          </BrowserRouter>
        </ConfirmProvider>
      </TooltipProvider>
    </PersistQueryClientProvider>
  </HelmetProvider>
);

export default App;
